import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import RenameWrapper from "@builder/components/general/Renamable";
import SelectInput from "@builder/components/general/SelectInput";
import { getUIComponent, isCopyable, isDeletable } from "@builder/store/slices/BuilderSlice/builderMap";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { copyNode, deleteNode, selectNode, updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { openLeftSidebar, setLeftInstance } from "@builder/store/slices/sidebarSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { BuildComponentKey, BuildNode } from "@common/build/types";
import classNames from "classnames";
import React, { useState } from "react";
import { NodeApi } from "react-arborist";
import { useTranslation } from "react-i18next";
import "./style.scss";


export const NodeHeight = 32;

const calcHeight = (node: NodeApi<TreeNode>): number => {
    if (!node.data.childrenFilterKey || !node.isOpen || !node.children) return NodeHeight;
    let height = NodeHeight - 3; //padding of the inside-row
    let current = node;
    while (current.data.childrenFilterKey && current.isOpen && current.children && current.children.length) {
        height += NodeHeight -1; // a little more padding for each child
        current = current.children[0] as NodeApi<TreeNode>;
    }
    return height;
};

export type TreeNode = {
    id: string;
    name: string;
    customName?: string;
    noCopy?: boolean;
    noDelete?: boolean;
    allChildren?: {
        id: string;
        name: string;
        customName?: string;
    }[];
    children?: TreeNode[];
    childrenFilterKey?: string;
    previewChildIndex?: number;
    order?: number;
    hidden?: boolean;
};

const SingleTreeNode: React.FC<{
    node: NodeApi<TreeNode>;
    dragHandle?: React.RefCallback<HTMLDivElement>;
    rest: any;
    root: string;
}> = ({ node, dragHandle, rest, root }) => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode);
    const leftSidebar = useAppSelector(state => state.sidebar.leftSidebar);
    const { t } = useTranslation();
    const [deleteMode, setDeleteMode] = useState(false);

    if (node.data.childrenFilterKey && node.data.allChildren?.length) {
        node.children?.forEach(child => {
            child.data.hidden = true;
        });
    }

    const handleMouseDown = (e: React.MouseEvent) => {
        const id = e.currentTarget.getAttribute("data-id");
        if (id && id !== selectedNode?.id) dispatch(selectNode(id));
        if (leftSidebar.instance !== "editElement") dispatch(setLeftInstance("editElement"));
        if (!leftSidebar.isOpen) dispatch(openLeftSidebar());
    };

    const handleDuplicate = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        dispatch(copyNode(id));
        e.stopPropagation();
    };

    const handleDeleteConfirm = (id: string) => {
        dispatch(deleteNode(id));
        setDeleteMode(false);
    };

    const handlePreviewChildChange = (index: number | string) => {
        const idx = Number(index);
        dispatch(
            updateNodeProperty({
                propertyPath: ["previewChildIndex"],
                value: idx
            })
        );

        if (node.data?.allChildren?.length) dispatch(selectNode(node.data.allChildren[idx].id));
    };

    const onClickDropdown = () => {
        if (node.data.children && node.data.children.length > 0) {
            dispatch(selectNode(node.data.children[0].id));
        }
    };

    //calc the height based on how many open children.,m granchildren etc have childrenFilterKey in a row under it.
   

    return (
        <div className={`build-node-wrapper ${node.data.hidden ? "hide" : ""}`} key={node.id} ref={dragHandle} {...rest}>
            <div className="build-node-row">
                {node.children && node.children.length > 0 && node.data.id !== root && (
                    <div className="build-node-row-button">
                        <Button
                            buttonStyle="transparent"
                            icon={{ src: "/angled-arrow-down.svg", side: "start" }}
                            style={{ height: "100%", padding: "0", rotate: node.isOpen ? "0deg" : "270deg" }}
                            onClick={() => node.toggle()}
                        />
                    </div>
                )}
                <div
                    className={classNames("build-node", { selected: node.id === selectedNode?.id }, { "delete-mode": deleteMode })}
                    data-id={node.id}
                    style={
                        node.data.childrenFilterKey && node.isOpen
                            ? {
                                  height: calcHeight(node),
                              }
                            : {}
                    }
                    onMouseDown={handleMouseDown}
                >
                    <div className="inside-row">
                        <img src={getUIComponent(node.data.name as BuildComponentKey).icon} className="build-node-icon" alt="" />
                        {deleteMode ? (
                            <OliverText
                                size="xs"
                                text={`${t("sidebarCollapse.Delete")} ${
                                    node.data.customName || t("builderElements." + node.data.name).toLowerCase()
                                }?`}
                            />
                        ) : (
                            <RenameWrapper
                                text={node.data.customName || t("builderElements." + node.data.name)}
                                textProps={{ size: "xs" }}
                                inputStyle={{ height: "20px" }}
                                onRename={name =>
                                    dispatch(
                                        updateNodeProperty({
                                            propertyPath: ["customName"],
                                            value: name
                                        })
                                    )
                                }
                            />
                        )}
                        <div className="two-buttons" style={deleteMode ? { opacity: 1 } : {}}>
                            {deleteMode ? (
                                <>
                                    <button className="copy-button" onMouseDown={() => setDeleteMode(false)}>
                                        <img src="/cross.svg" alt="" />
                                    </button>
                                    <button className="delete-button" onMouseDown={() => handleDeleteConfirm(node.id)}>
                                        <img src="/check.svg" alt="" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    {isCopyable(node.data as BuildNode) && (
                                        <button className="copy-button" onMouseDown={e => handleDuplicate(e, node.id)}>
                                            <img src="/copy-icon.svg" alt="" />
                                        </button>
                                    )}
                                    {isDeletable(node.data as BuildNode) && (
                                        <button
                                            className="delete-button"
                                            onMouseDown={e => {
                                                e.stopPropagation();
                                                setDeleteMode(true);
                                            }}
                                        >
                                            <img src="/trash.svg" alt="" />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {node.data.childrenFilterKey && node.data.allChildren?.length && node.isOpen ? (
                        <SelectInput
                        //adding selected if the current node in dropdown is the selected node (so check the ids of the previewChildIndex child:):
                            className={`select-child-preview ${ (node.data.previewChildIndex || node.data.previewChildIndex == 0) && node.children?.[node.data.previewChildIndex]?.id === selectedNode?.id ? "selected" : ""}`}
                            value={node.data.previewChildIndex ? String(node.data.previewChildIndex) : "0"}
                            options={node.data.allChildren?.reduce((acc, child, index) => {
                                acc[index] = child.customName || t("builderElements." + child.name);
                                return acc;
                            }, {} as Record<number, string>)}
                            callback={handlePreviewChildChange}
                            onClick={onClickDropdown}
                            noTranslate
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default SingleTreeNode;
